import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
// Verlet positions accumulate in RGBA float targets - see the module.
import { FLOAT_TEXTURES_OK } from '../float-textures.js';

// Shared-rig contract (see the framing addendum): Silk owns its two lights
// (one real front key with the shadow map, one analytic back light in the
// fragment shader) - the rig's beams would fight the fabric's own reading.
const RIG = 'forbidden';
export { RIG };


// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'silk',
  family: 'object',
  danceability: 0.40,
  // 0, not 1. requiresTier gates SELECTION, not capability: this file has
  // had a real tier-0 layout all along (see its TIER table). The radio page
  // settles at tier 0 on the hardware it actually runs on - measured on
  // judicael.com - so at 1 this was never offered there at all, which is
  // why none of the recent visualizers ever appeared on the web radio. The
  // cost is that weak devices now get it at its tier-0 layout rather than
  // not at all; the frame-time governor still owns the tier itself.
  requiresTier: 0,
  requiresFloatTextures: true,
  energyLevel: 0.38,
  bpmMin: 60,
  bpmMax: 140,
  movement: 0.45,
  tonality: 0.85,
  prefers: { dynamics: [0.35, 1.0], percussiveness: [0.0, 0.60] },
  enabled: FLOAT_TEXTURES_OK
};

// Silk: a suspended silk veil with the cover printed on it, moved by an air
// current, backlit, with an anisotropic sheen and real self-shadowing.
//
// The cloth is a GPU Verlet simulation: positions live in float ping-pong
// textures (FeedbackBuffer, extended to FloatType for exactly this), one
// integration pass plus three constraint families relaxed by Jacobi. All the
// discipline here is about NOT exploding: fixed timestep with an
// accumulator, a hard per-substep velocity clamp, damping, and stiffness in
// the narrow band where fabric neither vibrates nor turns to rubber.
//
// The wind is a FIELD, not a vector: a slow background flow, gaussian gusts
// born on bass rises that traverse the veil, and low-frequency turbulence
// for the surface shimmer. Every force is projected on the LOCAL NORMAL -
// face-on cloth takes the full wind, edge-on cloth takes none. Without that
// projection the sheet translates instead of undulating (criterion 2).

// --- cloth geometry -----------------------------------------------------------
const CLOTH_W = 5.5;
const CLOTH_H = 6.2;
const CAMERA_POS = [0, 2.8, 7.5];
const CAMERA_LOOK = [0, 2.8, 0];
const TOP_Y = 2.8 + CLOTH_H / 2;

// --- simulation ---------------------------------------------------------------
const FIXED_DT = 1 / 60;
const MAX_SUBSTEPS = 2;
const DAMPING = 0.985;
const GRAVITY_Y = -3.2;          // silk at this scale, not a lead sheet
const STIFFNESS_DEFAULT = 0.75;  // brief's band: 0.6 vibrationless .. 0.9 rubberless
const STIFFNESS_MIN = 0.6;
const STIFFNESS_MAX = 0.9;
const VEL_CLAMP = 0.15;          // per substep - the last wall against explosion
const PIN_FADE_S = 1.0;          // suspension-mode change: release over 1s

// --- wind ---------------------------------------------------------------------
const BASE_WIND = 0.30;
const BASE_LFO_S = 14;
const GUST_MAX = 3;              // ring buffer
const GUST_CROSS_MIN_S = 2.0;
const GUST_CROSS_MAX_S = 4.0;
// 1.0, and blowing AWAY from the camera. At 3.2 toward +Z the first gust
// threw the whole veil past the camera plane (nodes measured at z=11 with
// the lens at 7.5) and the frame went black.
const GUST_AMP = 1.0;
const GUST_RISE = 0.16;          // bands.low rise that births a gust
const GUST_REFRACTORY_S = 0.7;
const TURB_AMP = 0.9;            // scaled by bands.mid
const PHRASE_TURN_MIN = Math.PI / 6;   // 30..90 degrees per phrase
const PHRASE_TURN_MAX = Math.PI / 2;

// --- render -------------------------------------------------------------------
const ART_WASH = 0.12;           // silk print is never as saturated as the cover
// Highest MEAN luminance the print is allowed to reach on the veil. A white
// or near-white sleeve used to render the cloth invisible: the print went in
// at ~1.0, so the diffuse sat at full albedo, the backlight added at full
// strength on top of it and the satin on top of that - every one of the
// terms that normally MODEL the cloth (fold thinning, transmission angle,
// sheen) landed above the clipping point and came out as one flat white
// rectangle. The print is therefore scaled so its mean lands at or below
// this, which is a plain multiply and so leaves blacks at black: a white
// sleeve with dark type keeps its type, and the fabric gets the headroom
// back that makes it read as fabric. Only covers brighter than this are
// touched at all - a normal cover (mean ~0.35-0.5) passes through at 1.0.
const PRINT_MEAN_MAX = 0.55;
const BACK_INTENSITY = 0.55;
const SATIN_INTENSITY = 0.30;    // bands.high scales this
const ART_FADE_S = 1.5;
const SAFE_FLOOR = 0.5;
// Selvedge width, in grid uv. 0.6 of a cell at n=128 is ~0.026 world units,
// under 2 screen pixels at this framing - an antialiasing ramp, not a
// visible fade.
const SELVEDGE_CELLS = 0.6;

const TIER = {
  // Tier 0 exists only as a live-downgrade refuge (the orchestrator never
  // offers silk below tier 1 - requiresTier).
  0: { n: 64, iterations: 2, shadows: false },
  1: { n: 64, iterations: 2, shadows: false },
  2: { n: 128, iterations: 4, shadows: true }
};
const DEFAULT_TIER = 2;

const PIN_MODES = ['rideau', 'banniere', 'voile'];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// ============================================================================
// Simulation shaders - highp throughout: positions integrate for minutes and
// mediump's fp16 would visibly quantise the cloth within seconds.
// ============================================================================

const simVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// Everything the pin logic needs, shared by both sim passes. Pins are
// ANALYTIC - a function of grid uv and the suspension mode - not stored in
// the texture: a mode change is then just three uniforms, and the 1s release
// fade (criterion 7: the cloth FALLS, it never teleports) is a mix of the
// old pattern out and the new pattern in.
const PIN_CHUNK = `
  uniform float uModeA;      // 0 rideau, 1 banniere, 2 voile
  uniform float uModeB;
  uniform float uPinFade;    // 0 = all A, 1 = all B
  uniform float uPinEps;     // 1.5 texels - the top ROW, texel-centre proof

  float pinPattern(vec2 gridUv, float mode) {
    // NOT a 0.999 literal: the top row's texel CENTRE sits at (n-0.5)/n
    // (0.996 at n=128), so a fixed threshold above that pinned nothing and
    // the whole veil went into clean free fall - measured at y=-10 with
    // every neighbour distance still at rest.
    if (gridUv.y < 1.0 - uPinEps) return 0.0;
    if (mode < 0.5) return 1.0;               // rideau: the whole top edge
    if (mode < 1.5) {                         // banniere: the two top corners
      return (gridUv.x < 0.02 || gridUv.x > 0.98) ? 1.0 : 0.0;
    }
    return abs(gridUv.x - 0.5) < 0.02 ? 1.0 : 0.0;   // voile: one centre point
  }

  float pinStrength(vec2 gridUv) {
    return max(pinPattern(gridUv, uModeA) * (1.0 - uPinFade),
               pinPattern(gridUv, uModeB) * uPinFade);
  }

  vec3 restPosition(vec2 gridUv) {
    return vec3((gridUv.x - 0.5) * ${CLOTH_W.toFixed(2)},
                ${(TOP_Y - CLOTH_H).toFixed(2)} + gridUv.y * ${CLOTH_H.toFixed(2)},
                0.0);
  }
`;

const integrateFragmentShader = `
  precision highp float;

  uniform sampler2D uCurr;
  uniform sampler2D uPrev;
  uniform float uDt;
  uniform float uDamping;
  uniform float uTime;
  uniform vec2 uTexel;
  // wind field
  uniform vec3 uBaseWind;
  uniform float uTurbAmp;
  uniform vec4 uGustA;   // xy: centre in grid uv, z: birth time, w: amplitude
  uniform vec4 uGustB;
  uniform vec4 uGustC;
  uniform vec4 uGustDirSpeed[3];  // xy: direction (grid uv/s), z: sigma, w: unused

  varying vec2 vUv;

  ${PIN_CHUNK}

  // Low-frequency 3D-ish turbulence from three incommensurate sines - the
  // surface shimmer, not a physical model. Cheap and smooth.
  vec3 turbulence(vec3 p, float t) {
    return vec3(
      sin(p.y * 1.7 + t * 0.9) * cos(p.x * 1.3 + t * 0.7),
      sin(p.x * 1.1 + t * 1.1) * 0.4,
      sin(p.y * 2.1 + t * 0.8) * cos(p.x * 0.9 - t * 0.6)
    );
  }

  vec3 gustForce(vec4 gust, vec4 dirSpeed, vec2 gridUv, float t) {
    if (gust.w <= 0.0) return vec3(0.0);
    float age = t - gust.z;
    vec2 centre = gust.xy + dirSpeed.xy * age;
    vec2 d = gridUv - centre;
    float g = exp(-dot(d, d) / (2.0 * dirSpeed.z * dirSpeed.z));
    // The gust blows through the veil INTO the depth (-Z): the cloth bellies
    // away from the camera, where the backlight then reads through the folds.
    float fade = clamp(1.0 - age / 4.0, 0.0, 1.0);
    return vec3(0.0, 0.0, -1.0) * g * gust.w * fade;
  }

  void main() {
    vec4 curr = texture2D(uCurr, vUv);
    vec4 prev = texture2D(uPrev, vUv);

    // Local normal by central differences - needed HERE because the wind is
    // projected on it (criterion 2: undulation, not translation).
    vec3 xl = texture2D(uCurr, vUv - vec2(uTexel.x, 0.0)).xyz;
    vec3 xr = texture2D(uCurr, vUv + vec2(uTexel.x, 0.0)).xyz;
    vec3 yd = texture2D(uCurr, vUv - vec2(0.0, uTexel.y)).xyz;
    vec3 yu = texture2D(uCurr, vUv + vec2(0.0, uTexel.y)).xyz;
    vec3 N = cross(xr - xl, yu - yd);
    float nl = length(N);
    N = nl > 0.0001 ? N / nl : vec3(0.0, 0.0, 1.0);

    vec3 wind = uBaseWind
      + turbulence(curr.xyz, uTime) * uTurbAmp
      + gustForce(uGustA, uGustDirSpeed[0], vUv, uTime)
      + gustForce(uGustB, uGustDirSpeed[1], vUv, uTime)
      + gustForce(uGustC, uGustDirSpeed[2], vUv, uTime);
    // Projection on the local normal: face-on takes it all, edge-on none.
    vec3 accel = vec3(0.0, ${GRAVITY_Y.toFixed(2)}, 0.0) + N * dot(wind, N);

    vec3 vel = (curr.xyz - prev.xyz) * uDamping;
    vec3 next = curr.xyz + vel + accel * uDt * uDt;

    // The last wall: however wrong a frame goes, a node moves at most
    // VEL_CLAMP per substep.
    vec3 step = next - curr.xyz;
    float sl = length(step);
    if (sl > ${VEL_CLAMP.toFixed(3)}) step *= ${VEL_CLAMP.toFixed(3)} / sl;
    next = curr.xyz + step;

    float pin = pinStrength(vUv);
    next = mix(next, restPosition(vUv), pin);

    gl_FragColor = vec4(next, 0.0);
  }
`;

// One constraint family per pass: 4 neighbour offsets + one rest length,
// Jacobi-relaxed (read A, write B - you cannot write in place on a GPU).
const constraintFragmentShader = `
  precision highp float;

  uniform sampler2D uTex;
  uniform vec2 uTexel;
  uniform vec2 uOffsets[4];   // in texels
  uniform float uRest;
  uniform float uStiffness;

  varying vec2 vUv;

  ${PIN_CHUNK}

  void main() {
    vec3 pos = texture2D(uTex, vUv).xyz;
    vec3 corrected = pos;
    for (int i = 0; i < 4; i++) {
      vec2 nUv = vUv + uOffsets[i] * uTexel;
      // A neighbour outside the sheet exerts nothing.
      if (nUv.x < 0.0 || nUv.x > 1.0 || nUv.y < 0.0 || nUv.y > 1.0) continue;
      vec3 nPos = texture2D(uTex, nUv).xyz;
      vec3 d = nPos - pos;
      float dist = length(d);
      if (dist < 0.0001) continue;
      // The brief's exact update, per neighbour, accumulated - an extra
      // averaging factor here (an earlier 0.25) weakened the relaxation four
      // fold and the sheet crept to y=-10 under its own weight.
      corrected += d * ((dist - uRest) / dist) * 0.5 * uStiffness;
    }
    float pin = pinStrength(vUv);
    corrected = mix(corrected, restPosition(vUv), pin);
    gl_FragColor = vec4(corrected, 0.0);
  }
`;

// Background: soft dark gradient tinted by the palette, dithered.
const bgFragmentShader = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uAspect;
  varying vec2 vUv;
  void main() {
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
    float g = 1.0 - clamp(length(p) * 1.1, 0.0, 1.0);
    vec3 col = uTint * (0.03 + g * g * 0.05);
    vec2 fcw = mod(floor(gl_FragCoord.xy), 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    gl_FragColor = vec4(max(col + dith, vec3(0.0)), 1.0);
  }
`;

export class SilkVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._wire = urlParam('silkwire') === '1';
    const pin = urlParam('silkpin');
    this._forcedPin = pin && PIN_MODES.indexOf(pin) !== -1 ? pin : null;
    const stiff = parseFloat(urlParam('silkstiff'));
    this._stiffness = Number.isFinite(stiff)
      ? Math.max(STIFFNESS_MIN, Math.min(STIFFNESS_MAX, stiff)) : STIFFNESS_DEFAULT;
    this._gustDebug = urlParam('silkgust') === '1';
    // The cover is printed ON the veil - the DOM overlay would repeat it and
    // mask the cloth. scene-manager reads this per frame (see
    // _syncPostProfile) and fades the overlay out while Silk is on.
    this.hideOverlay = true;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    const layout = TIER[this._tier];
    this._n = layout.n;
    this._iterations = layout.iterations;

    // Two float ping-pong pairs (FeedbackBuffer extended for exactly this):
    // the pair ROLES alternate every substep - the pair holding the substep's
    // input becomes "previous", the other receives integration + Jacobi.
    const bufOpts = { width: this._n, height: this._n, type: THREE.FloatType, filter: THREE.NearestFilter };
    this._bufA = new FeedbackBuffer(bufOpts);
    this._bufB = new FeedbackBuffer(bufOpts);
    this._simReady = false;
    this._prevTexture = null;

    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;

    const texel = 1 / this._n;
    this._integrateUniforms = {
      uCurr: { value: null }, uPrev: { value: null },
      uDt: { value: FIXED_DT }, uDamping: { value: DAMPING },
      uTime: { value: 0 }, uTexel: { value: new THREE.Vector2(texel, texel) },
      uBaseWind: { value: new THREE.Vector3(BASE_WIND, 0, -BASE_WIND * 0.6) },
      uTurbAmp: { value: 0.4 },
      uGustA: { value: new THREE.Vector4(0, 0, -100, 0) },
      uGustB: { value: new THREE.Vector4(0, 0, -100, 0) },
      uGustC: { value: new THREE.Vector4(0, 0, -100, 0) },
      uGustDirSpeed: { value: [new THREE.Vector4(0.3, 0, 0.22, 0), new THREE.Vector4(0.3, 0, 0.22, 0), new THREE.Vector4(0.3, 0, 0.22, 0)] },
      uModeA: { value: 0 }, uModeB: { value: 0 }, uPinFade: { value: 1 },
      uPinEps: { value: 1.5 / this._n }
    };
    this._constraintUniforms = {
      uTex: { value: null }, uTexel: { value: new THREE.Vector2(texel, texel) },
      uOffsets: { value: [new THREE.Vector2(), new THREE.Vector2(), new THREE.Vector2(), new THREE.Vector2()] },
      uRest: { value: 0 }, uStiffness: { value: this._stiffness },
      uModeA: this._integrateUniforms.uModeA,
      uModeB: this._integrateUniforms.uModeB,
      uPinFade: this._integrateUniforms.uPinFade,
      uPinEps: this._integrateUniforms.uPinEps
    };

    this._simScene = new THREE.Scene();
    this._simQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({ uniforms: this._integrateUniforms, vertexShader: simVertexShader, fragmentShader: integrateFragmentShader, depthTest: false, depthWrite: false })
    );
    this._simQuad.frustumCulled = false;
    this._simScene.add(this._simQuad);
    this._constraintMaterial = new THREE.ShaderMaterial({ uniforms: this._constraintUniforms, vertexShader: simVertexShader, fragmentShader: constraintFragmentShader, depthTest: false, depthWrite: false });
    this._integrateMaterial = this._simQuad.material;

    // Constraint families: offsets in texels + rest length in world units.
    const stepX = CLOTH_W / (this._n - 1);
    const stepY = CLOTH_H / (this._n - 1);
    const stepD = Math.sqrt(stepX * stepX + stepY * stepY);
    this._families = [
      { offsets: [[1, 0], [-1, 0], [0, 1], [0, -1]], rest: (stepX + stepY) / 2 },
      { offsets: [[1, 1], [-1, 1], [1, -1], [-1, -1]], rest: stepD },
      { offsets: [[2, 0], [-2, 0], [0, 2], [0, -2]], rest: stepX + stepY }
    ];

    // --- the veil mesh -------------------------------------------------------
    this._artTexture = null;
    this._artTexturePrev = null;
    this._artMix = 1;
    this._buildVeil(layout);

    // --- lights --------------------------------------------------------------
    this._keyLight = new THREE.DirectionalLight(0xffffff, 1.35);
    this._keyLight.position.set(4.5, 5.5, 6.0);
    this._keyLight.target.position.set(0, 2.8, 0);
    scene.add(this._keyLight);
    scene.add(this._keyLight.target);
    if (layout.shadows) {
      this._keyLight.castShadow = true;
      this._keyLight.shadow.mapSize.set(1024, 1024);
      const sc = this._keyLight.shadow.camera;
      sc.left = -4.5; sc.right = 4.5; sc.top = 4.5; sc.bottom = -4.5;
      sc.near = 1; sc.far = 20;
      this._keyLight.shadow.bias = -0.002;
    }
    this._ambient = new THREE.AmbientLight(0xffffff, 0.55);
    scene.add(this._ambient);

    // --- background ----------------------------------------------------------
    this._bgUniforms = { uTint: { value: new THREE.Color(0x1a2030) }, uAspect: safeZoneUniforms.uAspect };
    this._bgMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({ uniforms: this._bgUniforms, vertexShader: simVertexShader, fragmentShader: bgFragmentShader, depthTest: false, depthWrite: false })
    );
    this._bgMesh.frustumCulled = false;
    this._bgMesh.renderOrder = -1000;
    scene.add(this._bgMesh);

    // --- state ---------------------------------------------------------------
    this._time = 0;
    this._accumulator = 0;
    this._paused = false;
    this._lowPrev = 0.5;
    this._lastGustAt = -10;
    this._gustIndex = 0;
    this._pinModeA = 0;
    this._pinModeB = 0;
    this._pinFadeT = 1;
    this._baseWindAngle = 0.3;
    this._windAngleFrom = 0.3;
    this._windAngleTo = 0.3;
    this._windTurnT = 1;
    this._windTurnDur = 1;
    this._lastPhraseIndex = null;
    this._lastTrackId = undefined;
    this._lastArtworkUrl = undefined;
    this._lastPaletteRef = undefined;
    this._backDir = new THREE.Vector3(0, 0, -1);
    this._shadowWasEnabled = null;
    this._dynamics = 0.5;
    this._midSmooth = 0.5;
    this._highSmooth = 0.5;

    if (this._forcedPin) {
      const idx = PIN_MODES.indexOf(this._forcedPin);
      this._pinModeA = idx; this._pinModeB = idx;
      this._integrateUniforms.uModeA.value = idx;
      this._integrateUniforms.uModeB.value = idx;
    }
  }

  _buildVeil(layout) {
    const geo = new THREE.PlaneGeometry(1, 1, this._n - 1, this._n - 1);
    const mat = this._wire
      ? new THREE.MeshBasicMaterial({ wireframe: true, color: 0x66ffcc })
      : new THREE.MeshStandardMaterial({
        color: 0xffffff, roughness: 0.62, metalness: 0.0,
        side: THREE.DoubleSide,
        // For the selvedge alpha ramp below. depthWrite stays on, so folds
        // still occlude each other correctly - only the thin border blends.
        transparent: true
      });
    this._veilUniforms = {
      uPosTex: { value: null },
      uTexel: { value: new THREE.Vector2(1 / this._n, 1 / this._n) },
      uArt: { value: null },
      uArtPrev: { value: null },
      uArtMix: { value: 1 },
      uHasArt: { value: 0 },
      // See PRINT_MEAN_MAX. 1.0 = the cover goes on the silk untouched,
      // which is the case for every cover that is not unusually pale.
      uArtLevel: { value: 1 },
      uBackDir: { value: new THREE.Vector3(0, 0.1, -1).normalize() },
      uBackColor: { value: new THREE.Color(0xffe0b0) },
      uSatin: { value: SATIN_INTENSITY },
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather,
      uSafeAspect: safeZoneUniforms.uAspect
    };
    const veilUniforms = this._veilUniforms;

    // The vertex-side surgery is identical for every material that renders
    // the veil - including the DEPTH material the shadow map uses. Without
    // the same displacement there, the veil would shadow as the flat
    // rectangle it started as.
    const injectVertex = (shader) => {
      Object.assign(shader.uniforms, veilUniforms);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>
          uniform sampler2D uPosTex;
          uniform vec2 uTexel;
          varying vec2 vGridUv;
          varying vec3 vWorldNormal2;
          varying vec3 vTangent;
          varying float vFold;
          varying float vSafeS;
          vec3 clothPos(vec2 gridUv) {
            return texture2D(uPosTex, gridUv).xyz;
          }`)
        .replace('#include <beginnormal_vertex>', `
          vec2 gUv = uv;
          vec3 cXl = clothPos(gUv - vec2(uTexel.x, 0.0));
          vec3 cXr = clothPos(gUv + vec2(uTexel.x, 0.0));
          vec3 cYd = clothPos(gUv - vec2(0.0, uTexel.y));
          vec3 cYu = clothPos(gUv + vec2(0.0, uTexel.y));
          vec3 objectNormal = normalize(cross(cXr - cXl, cYu - cYd));
          // Fold density: how compressed the sheet is locally - the norm of
          // the position gradient against its rest value. Doubled cloth
          // transmits less backlight.
          float gradLen = (length(cXr - cXl) + length(cYu - cYd)) * 0.5;
          vFold = clamp(1.0 - gradLen / ${(2 * (CLOTH_W / 128 + CLOTH_H / 128) / 2).toFixed(4)}, 0.0, 1.0);
          vGridUv = gUv;
          vWorldNormal2 = normalize(mat3(modelMatrix) * objectNormal);
          // The weft tangent - the grid's U axis as it is actually deformed -
          // for the anisotropic sheen. THE tangent, not an approximation from
          // the normal (criterion 4: the sheen follows the weave).
          vTangent = normalize(mat3(modelMatrix) * (cXr - cXl));`)
        .replace('#include <begin_vertex>', `
          vec3 transformed = clothPos(uv);`)
        .replace('#include <project_vertex>', `#include <project_vertex>
          {
            vec2 ndc2 = gl_Position.xy / max(gl_Position.w, 0.0001);
            vec2 sxy = vec2(ndc2.x * uSafeAspect, ndc2.y);
            vec2 q2 = abs(sxy - uSafeCenter) - uSafeHalfSize;
            float d2 = length(max(q2, 0.0)) + min(max(q2.x, q2.y), 0.0);
            vSafeS = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d2));
          }`);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>
          uniform float uSafeAspect;
          uniform vec2 uSafeCenter;
          uniform vec2 uSafeHalfSize;
          uniform float uSafeFeather;`);
    };

    if (!this._wire) {
      mat.onBeforeCompile = (shader) => {
        injectVertex(shader);
        shader.fragmentShader = shader.fragmentShader
          .replace('#include <common>', `#include <common>
            uniform sampler2D uArt;
            uniform sampler2D uArtPrev;
            uniform float uArtMix;
            uniform float uHasArt;
            uniform float uArtLevel;
            uniform vec3 uBackDir;
            uniform vec3 uBackColor;
            uniform float uSatin;
            varying vec2 vGridUv;
            varying vec3 vWorldNormal2;
            varying vec3 vTangent;
            varying float vFold;
            varying float vSafeS;`)
          .replace('#include <map_fragment>', `
            if (uHasArt > 0.5) {
              vec4 artNew = texture2D(uArt, vGridUv);
              vec4 artOld = texture2D(uArtPrev, vGridUv);
              vec4 art = mix(artOld, artNew, uArtMix);
              // A silk print is never as saturated as the cover itself.
              // uArtLevel (see PRINT_MEAN_MAX) is applied to the print
              // BEFORE the wash, so a pale sleeve arrives already carrying
              // midtones instead of being pushed further toward white.
              diffuseColor.rgb *= mix(art.rgb * uArtLevel, vec3(1.0), ${ART_WASH.toFixed(2)});
            }`)
          .replace('#include <opaque_fragment>', `#include <opaque_fragment>
          {
            vec3 N2 = normalize(vWorldNormal2) * (gl_FrontFacing ? 1.0 : -1.0);
            // BACKLIGHT: transmission depends on the angle to the source
            // behind, thinned where the sheet folds onto itself.
            float transmit = max(dot(-N2, normalize(uBackDir)), 0.0);
            // Same uArtLevel as the diffuse print above: this additive term
            // is the one that actually blew out on a white sleeve, so it
            // has to come down with it rather than re-adding at full
            // strength what the diffuse just gave up.
            vec3 art2 = uHasArt > 0.5 ? mix(texture2D(uArtPrev, vGridUv), texture2D(uArt, vGridUv), uArtMix).rgb * uArtLevel : vec3(0.9);
            gl_FragColor.rgb += uBackColor * art2 * (transmit * ${BACK_INTENSITY.toFixed(2)} * (1.0 - vFold));
            // SATIN: Kajiya-Kay along the weft (grid U axis), the same
            // anisotropic term VinylGroove uses for its grooves.
            vec3 V2 = normalize(vViewPosition);
            vec3 L2 = normalize(vec3(0.6, 0.35, 0.8));
            vec3 T2 = normalize(vTangent);
            float dTL = dot(T2, L2);
            float dTV = dot(T2, V2);
            float kk = sqrt(max(1.0 - dTL * dTL, 0.0)) * sqrt(max(1.0 - dTV * dTV, 0.0)) - dTL * dTV;
            float s2 = kk * kk; s2 = s2 * s2; s2 = s2 * s2;   // ^8, unrolled
            gl_FragColor.rgb += vec3(1.0, 0.98, 0.94) * s2 * uSatin;
            gl_FragColor.rgb *= vSafeS;
            // SELVEDGE. The chain runs antialias:false with no MSAA anywhere
            // (see scene-manager), so the veil's silhouette against the dark
            // background stair-steps - worst exactly where the user saw it,
            // on the near-straight left, right and top edges (the bottom is
            // wavy, which hides it). The border alpha ramps over the outer
            // fraction of a grid cell, which reads as the soft edge of real
            // fabric and costs one min() - and unlike MSAA it is free.
            vec2 edge = min(vGridUv, 1.0 - vGridUv);
            gl_FragColor.a *= clamp(min(edge.x, edge.y) / ${(SELVEDGE_CELLS / this._n).toFixed(6)}, 0.0, 1.0);
          }`);
      };
      // The shadow map renders the veil through a DEPTH material - it needs
      // the same displaced vertices or the shadows are those of a flat sheet.
      const depthMat = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
      depthMat.onBeforeCompile = (shader) => { injectVertex(shader); };
      this._depthMaterial = depthMat;
    } else {
      mat.onBeforeCompile = (shader) => { injectVertex(shader); };
    }

    this._veil = new THREE.Mesh(geo, mat);
    this._veil.frustumCulled = false;
    if (!this._wire && TIER[this._tier].shadows) {
      this._veil.castShadow = true;
      this._veil.receiveShadow = true;
      this._veil.customDepthMaterial = this._depthMaterial;
    }
    const self = this;
    this._veil.onBeforeRender = function (renderer) { self._runSim(renderer); };
    this._scene.add(this._veil);
  }

  // Seeds both position textures with the rest pose.
  _seed(renderer) {
    const n = this._n;
    const data = new Float32Array(n * n * 4);
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const i = (y * n + x) * 4;
        data[i] = (x / (n - 1) - 0.5) * CLOTH_W;
        data[i + 1] = (TOP_Y - CLOTH_H) + (y / (n - 1)) * CLOTH_H;
        data[i + 2] = 0;
        data[i + 3] = 0;
      }
    }
    const seedTex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat, THREE.FloatType);
    seedTex.needsUpdate = true;
    // Blit the seed into all four targets via a copy pass.
    const copyMat = new THREE.ShaderMaterial({
      uniforms: { uTex: { value: seedTex } },
      vertexShader: simVertexShader,
      fragmentShader: 'precision highp float; uniform sampler2D uTex; varying vec2 vUv; void main() { gl_FragColor = texture2D(uTex, vUv); }',
      depthTest: false, depthWrite: false
    });
    const prevMat = this._simQuad.material;
    this._simQuad.material = copyMat;
    for (const rt of [this._bufA.read, this._bufA.write, this._bufB.read, this._bufB.write]) {
      renderer.setRenderTarget(rt);
      renderer.render(this._simScene, this._ortho);
    }
    this._simQuad.material = prevMat;
    copyMat.dispose();
    seedTex.dispose();
    this._prevTexture = this._bufB.read.texture;
    this._currBuf = this._bufA;
    this._veilUniforms.uPosTex.value = this._bufA.read.texture;
  }

  // One fixed-dt substep: integrate then relax the three families.
  _substep(renderer) {
    const target = this._currBuf === this._bufA ? this._bufB : this._bufA;

    // integrate: (curr, prev) -> target.write
    this._simQuad.material = this._integrateMaterial;
    this._integrateUniforms.uCurr.value = this._currBuf.read.texture;
    this._integrateUniforms.uPrev.value = this._prevTexture;
    renderer.setRenderTarget(target.write);
    renderer.render(this._simScene, this._ortho);
    target.swap();

    // Jacobi: families x iterations, ping-ponging inside `target`.
    this._simQuad.material = this._constraintMaterial;
    for (let it = 0; it < this._iterations; it++) {
      for (const fam of this._families) {
        const u = this._constraintUniforms;
        for (let i = 0; i < 4; i++) u.uOffsets.value[i].set(fam.offsets[i][0], fam.offsets[i][1]);
        u.uRest.value = fam.rest;
        u.uTex.value = target.read.texture;
        renderer.setRenderTarget(target.write);
        renderer.render(this._simScene, this._ortho);
        target.swap();
      }
    }

    // Roles rotate: this substep's input becomes "previous".
    this._prevTexture = this._currBuf.read.texture;
    this._currBuf = target;
  }

  _runSim(renderer) {
    if (!this._simReady) {
      this._bufA.ensureSize(renderer);
      this._bufB.ensureSize(renderer);
      this._seed(renderer);
      this._simReady = true;
      // Shadow maps for the whole page: on only while Silk is alive.
      this._shadowWasEnabled = renderer.shadowMap.enabled;
      if (TIER[this._tier].shadows && !this._wire) {
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFShadowMap;
      }
      this._rendererRef = renderer;
    }
    if (this._paused) return;

    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;

    let steps = 0;
    while (this._accumulator >= FIXED_DT && steps < MAX_SUBSTEPS) {
      this._integrateUniforms.uTime.value = this._time - this._accumulator;
      this._substep(renderer);
      this._accumulator -= FIXED_DT;
      steps++;
    }
    // A long frame beyond the substep budget DROPS the surplus rather than
    // integrating it: same discipline as Ripple - a variable dt explodes
    // cloth on the first slow frame.
    if (this._accumulator > FIXED_DT * MAX_SUBSTEPS) this._accumulator = FIXED_DT * MAX_SUBSTEPS;

    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;

    this._veilUniforms.uPosTex.value = this._currBuf.read.texture;
  }

  _spawnGust(strength) {
    const u = this._integrateUniforms;
    const slots = [u.uGustA, u.uGustB, u.uGustC];
    const slot = slots[this._gustIndex % GUST_MAX];
    const ds = u.uGustDirSpeed.value[this._gustIndex % GUST_MAX];
    this._gustIndex++;
    const fromLeft = Math.random() < 0.5;
    const crossTime = lerp(GUST_CROSS_MIN_S, GUST_CROSS_MAX_S, Math.random());
    const speed = 1.4 / crossTime;   // grid-uv units per second, margin included
    slot.value.set(fromLeft ? -0.2 : 1.2, 0.25 + Math.random() * 0.5, this._time, GUST_AMP * strength * this._dynamics);
    ds.set(fromLeft ? speed : -speed, (Math.random() - 0.5) * 0.08, 0.20 + Math.random() * 0.08, 0);
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;   // freeze: neither time nor the accumulator move
    this._time += deltaTime;
    this._accumulator += deltaTime;

    const director = profile.director;
    const bands = director && director.bands;
    const low = bands && bands.low != null ? clamp01(bands.low) : 0.5;
    const mid = bands && bands.mid != null ? clamp01(bands.mid) : 0.5;
    const high = bands && bands.high != null ? clamp01(bands.high) : 0.5;
    const k = 1 - Math.exp(-deltaTime / 0.2);
    this._midSmooth += (mid - this._midSmooth) * k;
    this._highSmooth += (high - this._highSmooth) * k;
    const character = director && director.character;
    this._dynamics = lerp(0.6, 1.4, clamp01(character && character.dynamics != null ? character.dynamics : 0.5));

    // Gusts are born on bass RISES, with a refractory gap.
    if ((low - this._lowPrev) > GUST_RISE && (this._time - this._lastGustAt) > GUST_REFRACTORY_S) {
      this._lastGustAt = this._time;
      this._spawnGust(0.6 + low * 0.8);
    }
    this._lowPrev += (low - this._lowPrev) * Math.min(1, deltaTime * 6);
    if (this._gustDebug) { this._gustDebug = false; this._spawnGust(1.2); }

    // Background wind: slow LFO on strength, phrase-driven direction turns.
    const phraseIndex = profile.phraseIndex;
    if (phraseIndex != null && this._lastPhraseIndex != null && phraseIndex !== this._lastPhraseIndex) {
      const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
      this._windAngleFrom = this._baseWindAngle;
      this._windAngleTo = this._baseWindAngle
        + lerp(PHRASE_TURN_MIN, PHRASE_TURN_MAX, Math.random()) * (Math.random() < 0.5 ? -1 : 1);
      this._windTurnT = 0;
      this._windTurnDur = Math.max(0.4, 2 * (bpm ? 60 / bpm : 0.5));
    }
    this._lastPhraseIndex = phraseIndex;
    if (this._windTurnT < 1) {
      this._windTurnT = Math.min(1, this._windTurnT + deltaTime / this._windTurnDur);
      this._baseWindAngle = lerp(this._windAngleFrom, this._windAngleTo, this._windTurnT);
    }
    const lfo = 0.6 + 0.4 * Math.sin((this._time / BASE_LFO_S) * Math.PI * 2);
    this._integrateUniforms.uBaseWind.value.set(
      Math.cos(this._baseWindAngle) * BASE_WIND * lfo,
      0.08 * lfo,
      -(0.55 + 0.45 * Math.sin(this._baseWindAngle)) * BASE_WIND * lfo
    );
    this._integrateUniforms.uTurbAmp.value = TURB_AMP * this._midSmooth;
    this._veilUniforms.uSatin.value = SATIN_INTENSITY * (0.4 + 1.2 * this._highSmooth);
    this._constraintUniforms.uStiffness.value = this._stiffness;

    // Suspension-mode change: only on a TRACK change, released over 1s.
    if (profile.trackId !== this._lastTrackId) {
      const first = this._lastTrackId === undefined;
      this._lastTrackId = profile.trackId;
      if (!first && this._forcedPin == null) {
        const next = Math.floor(Math.random() * PIN_MODES.length);
        if (next !== this._pinModeB) {
          this._pinModeA = this._pinModeB;
          this._pinModeB = next;
          this._pinFadeT = 0;
        }
      }
      if (!first) this._artMixFrom = 0;
    }
    if (this._pinFadeT < 1) {
      this._pinFadeT = Math.min(1, this._pinFadeT + deltaTime / PIN_FADE_S);
      this._integrateUniforms.uModeA.value = this._pinModeA;
      this._integrateUniforms.uModeB.value = this._pinModeB;
      this._integrateUniforms.uPinFade.value = this._pinFadeT;
    }

    // Artwork: crossfades on the veil over 1.5s, the simulation never stops.
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) {
        const self = this;
        loadArtworkTexture(profile.artworkUrl, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true },
          function (tex) {
            if (self._artTexturePrev) self._artTexturePrev.dispose();
            self._artTexturePrev = self._artTexture || tex;
            self._artTexture = tex;
            self._veilUniforms.uArt.value = tex;
            self._veilUniforms.uArtPrev.value = self._artTexturePrev;
            self._veilUniforms.uHasArt.value = 1;
            self._artMix = 0;
          }, function () {});
      }
    }
    if (this._artMix < 1) {
      this._artMix = Math.min(1, this._artMix + deltaTime / ART_FADE_S);
      this._veilUniforms.uArtMix.value = this._artMix;
    }

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const dom = profile.artworkPalette && profile.artworkPalette.dominantColors
        && profile.artworkPalette.dominantColors.length
        ? profile.artworkPalette.dominantColors[0] : 0x1a2030;
      this._bgUniforms.uTint.value.set(dom);
      // The print's own level, from the cover's MEASURED mean luminance -
      // artwork-palette.js already computes it over every sampled pixel, so
      // this needs no second read of the image. See PRINT_MEAN_MAX.
      const meanLum = profile.artworkPalette && profile.artworkPalette.brightness != null
        ? profile.artworkPalette.brightness
        : null;
      this._veilUniforms.uArtLevel.value = meanLum != null && meanLum > PRINT_MEAN_MAX
        ? PRINT_MEAN_MAX / meanLum
        : 1;
      const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
      if (vivid && vivid.length) {
        this._veilUniforms.uBackColor.value.set(vivid[0]).lerp(new THREE.Color(1, 0.93, 0.8), 0.45);
      }
    }

    // progress -> the back light climbs over the track, changing the angle.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    this._veilUniforms.uBackDir.value.set(0.15, lerp(-0.25, 0.75, progress), -1).normalize();
  }

  dispose() {
    if (this._rendererRef && this._shadowWasEnabled != null) {
      this._rendererRef.shadowMap.enabled = this._shadowWasEnabled;
    }
    this._scene.remove(this._veil);
    this._scene.remove(this._bgMesh);
    this._scene.remove(this._keyLight);
    this._scene.remove(this._keyLight.target);
    this._scene.remove(this._ambient);
    this._veil.geometry.dispose();
    this._veil.material.dispose();
    if (this._depthMaterial) this._depthMaterial.dispose();
    this._bgMesh.geometry.dispose();
    this._bgMesh.material.dispose();
    this._simQuad.geometry.dispose();
    this._integrateMaterial.dispose();
    this._constraintMaterial.dispose();
    this._bufA.dispose();
    this._bufB.dispose();
    if (this._artTexture) this._artTexture.dispose();
    if (this._artTexturePrev && this._artTexturePrev !== this._artTexture) this._artTexturePrev.dispose();
  }
}
